const HERE_API_KEY = process.env.REACT_APP_HERE_MAPS_API_KEY;

/**
 * arma la URL estática del "último punto"
 * - recibe un objeto con lat/lon
 *   { Latitud: number, Longitud: number }
 *   ó { LastMsgLat, LastMsgLong }
 * - fallback al contrato si viene con LastMsgLat / LastMsgLong
 */
export function buildUltimoPuntoUrl({ Latitud, Longitud, LastMsgLat, LastMsgLong }) {
    if (!HERE_API_KEY) {
        console.error("HERE_API_KEY no definida");
        return null;
    }

    // tolera ambas formas
    const lat = Latitud ?? LastMsgLat;
    const lng = Longitud ?? LastMsgLong;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    // construimos el geojson con un solo punto (marcador grande)
    const geojsonPoint = encodeURIComponent(
        JSON.stringify({
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {
                        marker: "cp",
                        size: "large",
                    },
                    geometry: {
                        type: "Point",
                        coordinates: [lng, lat], // HERE usa LONG,LAT
                    },
                },
            ],
        })
    );

    // misma forma que usas en StaticMaps2.updateImage / updateImageAjustada
    const size = "330x330";
    const zoom = 16;

    const url =
        `https://image.maps.hereapi.com/mia/v3/base/mc/center:${lat},${lng};zoom=${zoom}` +
        `/${size}/png8?apikey=${HERE_API_KEY}` +
        `&style=explore.satellite.day` +
        `&geojson=${geojsonPoint}`;

    return url;
}


/**
 * arma la URL estática del recorrido completo
 * - recibe un array de puntos: [{ Latitud, Longitud }, ...]
 *   tal como setMapRecorrido(recorrido) en StaticsMapsRecorrido2
 * - reproduce calculateCenter() y calculateZoom() que ya tienes
 */
export function buildRecorridoUrl(recorridoArray) {
    if (!HERE_API_KEY) {
        console.error("HERE_API_KEY no definida");
        return null;
    }

    if (!Array.isArray(recorridoArray) || recorridoArray.length === 0) {
        return null;
    }

    // limpiar puntos inválidos
    const valid = recorridoArray.filter(p =>
        Number.isFinite(p.Latitud) &&
        Number.isFinite(p.Longitud) &&
        p.Latitud !== 0 &&
        p.Longitud !== 0
    );
    if (valid.length === 0) {
        return null;
    }

    // === calculateCenter() ===
    const lats = valid.map(p => p.Latitud);
    const lngs = valid.map(p => p.Longitud);

    const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2;
    const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2;

    // === calculateZoom() (tu versión basada en primer y último punto) ===
    let zoom = 12;
    if (valid.length >= 2) {
        const a = valid[0];
        const b = valid[valid.length - 1];

        const lat1 = Number(a.Latitud);
        const lon1 = Number(a.Longitud);
        const lat2 = Number(b.Latitud);
        const lon2 = Number(b.Longitud);

        if ([lat1, lon1, lat2, lon2].every(Number.isFinite)) {
            const span = Math.max(Math.abs(lat2 - lat1), Math.abs(lon2 - lon1));

            if (span > 50) zoom = 4;
            else if (span > 20) zoom = 5;
            else if (span > 10) zoom = 5;
            else if (span > 7) zoom = 5.5;
            else if (span > 5) zoom = 6;
            else if (span > 3) zoom = 6;
            else if (span > 2) zoom = 7;
            else if (span > 1) zoom = 8.5;
            else if (span > 0.91) zoom = 8.5;
            else if (span > 0.5) zoom = 9;
            else if (span > 0.3) zoom = 9.5;
            else if (span > 0.2) zoom = 10;
            else if (span > 0.1) zoom = 11;
            else if (span > 0.08) zoom = 12;
            else if (span > 0.05) zoom = 12;
            else if (span > 0.01) zoom = 13;
            else if (span > 0.005) zoom = 14.5;
            else zoom = 16;

            zoom = Math.min(17, Math.max(3, zoom));
        }
    }

    // geojson LineString (idéntico a tu updateImage() de StaticsMapsRecorrido2)
    const geojsonLine = encodeURIComponent(
        JSON.stringify({
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {
                        color: "#0000FFFF",
                        width: "6",
                        "outline-color": "#000000FF",
                        "outline-width": "0px",
                    },
                    geometry: {
                        type: "LineString",
                        coordinates: valid.map(p => [p.Longitud, p.Latitud]), // LONG,LAT
                    },
                },
            ],
        })
    );

    const size = "330x330";

    const url =
        `https://image.maps.hereapi.com/mia/v3/base/mc/center:${centerLat},${centerLng};zoom=${zoom}` +
        `/${size}/png8?apikey=${HERE_API_KEY}` +
        `&style=explore.day` +
        `&geojson=${geojsonLine}`;

    return url;
}
